"use client";
import { useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getBaseUrl } from "@/util/get-base-url";
import { toPriceString } from "@/util/toPriceString";
import { formatCurrencyFormat } from "@/util/formatCurrencyFormat";
import CollectionListItemClient from "@/components/collections/collection-list-favorite-client"
import "./product-showboxes.css";

interface ProductShowBoxesProps {
    productsGroupData: any;
    loc: string;
    colorStyle: any;
    shopCurrency?: any;
    level: string;
    tabEnabled?: boolean;
    text?: any;
    priorityImage?: boolean;
}

const ProductShowBoxes = ({productsGroupData, loc, colorStyle, shopCurrency, level, tabEnabled, text, priorityImage}: ProductShowBoxesProps) => {

    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, dragFree: true });
    const [emblaTab] = useEmblaCarousel({ dragFree: true });
    const scrollNext = () => {
        if (emblaApi) emblaApi.scrollNext();
    };
    const scrollPrev = () => {
        if (emblaApi) emblaApi.scrollPrev();
    };

    const [groupId, setGroupId] = useState<number>(0);

    return (
        <div className="w-full flex flex-col justify-center items-center gap-6">
            {productsGroupData.tabEnable && <div className="w-full flex flex-col justify-center items-center gap-1">
                <div className="embla">
                    <div className="embla overflow-hidden" ref={emblaTab}>
                        <div className="w-full flex flex-row flex-nowrap sm:justify-start justify-start items-center gap-6">
                            {productsGroupData.productGroupItem.map((group: any, index: number) => ((group && group.attributes && group.attributes.displayName) ?    
                                <div 
                                    key={group.id}
                                    className={`py-4 font-semibold text-lg leading-[100%] text-center inline-flex whitespace-nowrap
                                                px-6 rounded-full cursor-pointer dynamic-bg dynamic-color`}
                                    style={{
                                        "--dynamic-bg": groupId === index ? colorStyle.buttonBgHover || "#254b7c" : "transparent",
                                        "--dynamic-hover-bg": colorStyle.buttonBgHover || "#254b7c",
                                        "--dynamic-color": groupId === index ? colorStyle.buttonTextHover || "#ffffff" : colorStyle.link || "#009eca" ,
                                        "--dynamic-hover-color": colorStyle.buttonTextHover || "#ffffff",
                                        "--dynamic-focus-color": colorStyle.buttonTextHover || "#ffffff",
                                    } as React.CSSProperties}
                                    onClick={() => {
                                        setGroupId(index);
                                    }}
                                >
                                    {group.attributes.displayName}
                                </div>  : null
                            ))} 
                        </div>
                    </div>
                </div>
                <div 
                    className="h-px w-full my-4"
                    style={{
                        backgroundColor: colorStyle ? colorStyle.text : "#3d3d3d",
                        opacity: 0.2
                    }}
                >
                </div>
            </div>
            }
            <div className="w-full flex flex-col justify-center items-center gap-10">
                {(productsGroupData.productGroupItem[groupId].productList && productsGroupData.productGroupItem[groupId].productList.length > 0) &&
                <>
                    <div className="embla">
                        <div className="embla" ref={emblaRef}>
                            <div className="embla__container" style={{ display: "flex", gap: "16px"}}>
                                {productsGroupData.productGroupItem[groupId].productList.map((product: any) => (
                                    (product && product.productVariant && product.productVariant.length > 0) && (
                                    <div 
                                        key={product.id} 
                                        className="product-showboxes flex flex-col gap-5"
                                    >
                                        <div
                                            className="flex flex-col gap-6 justify-start items-start w-full h-full"
                                        >
                                            <div
                                                className="relative w-full flex justify-center items-center aspect-square min-h-240 sm:min-h-480"
                                                style={{ maxWidth: "100%" }}
                                            >
                                                <a href={`/products/${product.handle}`} className="w-full h-full">
                                                    <picture>
                                                        <source
                                                            srcSet={product.featuredImageUrl != "" ? `${getBaseUrl(product.featuredImageUrl || "")}.avif` : "/assets/images/demo/web_logo_white.svg"}
                                                            type="image/avif"
                                                        />
                                                        <source
                                                            srcSet={product.featuredImageUrl != "" ? `${getBaseUrl(product.featuredImageUrl || "")}.webp` : "/assets/images/demo/web_logo_white.svg"}
                                                            type="image/webp"
                                                        />
                                                        <img
                                                            src={product.featuredImageUrl != "" ? product.featuredImageUrl : "/assets/images/demo/web_logo_white.svg"}
                                                            className={`w-full h-full object-contain bg-white rounded-3xl`}
                                                            width={480}
                                                            height={480}
                                                            loading={priorityImage ? "eager" : "lazy"}
                                                            alt={product.handle + "-image"}
                                                        />
                                                    </picture>
                                                </a>
                                                <CollectionListItemClient productHandle={product.handle} refresh={true} />
                                            </div>
                                            <div className="flex flex-col gap-5 justify-between w-full h-full">
                                                <div className="flex flex-col gap-1 w-[calc(25% - 16px)] ">
                                                    <a  href={`/products/${product.handle}`}
                                                        className="font-semibold xl:text-lg text-sm leading-8 hover:opacity-50"
                                                        style={{
                                                            color: colorStyle ? colorStyle.text : "#3d3d3d"
                                                        }}>
                                                        <span className="line-clamp-2">{product.attributes.displayName || ""}</span>
                                                    </a>
                                                </div>
                                                {product.productVariant[0].show_price ? (
                                                    <>
                                                        {level == "BP" &&
                                                            <div className="flex flex-row items-center flex-wrap gap-2 min-h-8">
                                                                <span 
                                                                    className="font-semibold xl:text-2xl lg:text-xl text-lg leading-8"
                                                                    style={{
                                                                        color: colorStyle ? colorStyle.text : "#3d3d3d"
                                                                    }}
                                                                >{formatCurrencyFormat(toPriceString(product.productVariant[0].comparePriceTrial                                                    ), shopCurrency)}</span>
                                                                
                                                                {product.productVariant[0].priceTrial != product.productVariant[0].comparePriceTrial && 
                                                                    <span 
                                                                        className="line-through font-normal xl:text-base lg:text-sm text-xs leading-8"
                                                                        style={{
                                                                            color: colorStyle ? colorStyle.text : "#3d3d3d",
                                                                            opacity: 0.5
                                                                        }}
                                                                    >
                                                                        {formatCurrencyFormat(toPriceString(product.productVariant[0].priceTrial), shopCurrency)}
                                                                    </span>
                                                                }
                                                            </div>
                                                        }
                                                        {(level == "NORMAL" || level == "GUEST") &&
                                                            <div className="flex flex-row items-center flex-wrap gap-2 min-h-8">
                                                                <span 
                                                                    className="font-semibold xl:text-2xl lg:text-xl text-lg leading-8"
                                                                    style={{
                                                                        color: colorStyle ? colorStyle.text : "#3d3d3d"
                                                                    }}
                                                                >{formatCurrencyFormat(toPriceString(product.productVariant[0].comparePriceFull), shopCurrency)}</span>
                                                                {product.productVariant[0].priceFull != product.productVariant[0].comparePriceFull && 
                                                                    <span 
                                                                        className="line-through font-normal text-base leading-8"
                                                                        style={{
                                                                            color: colorStyle ? colorStyle.text : "#3d3d3d",
                                                                            opacity: 0.5
                                                                        }}
                                                                    >
                                                                        {formatCurrencyFormat(toPriceString(product.productVariant[0].priceFull), shopCurrency)}
                                                                    </span>
                                                                }
                                                            </div>
                                                        }
                                                    </>
                                                ) : (
                                                    <div className="min-h-8"></div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    )
                                ))}
                            </div>
                        </div>
                        <div className="w-full flex justify-between items-center">
                            {productsGroupData.productGroupItem[groupId].productList.length > 4 && (<div className="flex w-1/3 justify-end mt-20"></div>)}
                            {(productsGroupData.productGroupItem[groupId].buttonAttributes 
                                && productsGroupData.productGroupItem[groupId].enableButton
                            ) && (
                                <div className="w-full flex justify-center mt-20">
                                    <a href={productsGroupData.productGroupItem[groupId].buttonAttributes.linkUrl || ""} aria-label="button">
                                        <button
                                            className="min-w-12 min-h-12 w-fit h-fit py-4 px-10 rounded-full font-medium px-10 dynamic-bg dynamic-color"
                                            style={
                                                {
                                                    "--dynamic-bg": colorStyle?.buttonBgNormal || "#006ef2",
                                                    "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                                    "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                                    "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                                    "--dynamic-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                                                } as React.CSSProperties
                                            }>
                                            {productsGroupData.productGroupItem[groupId].buttonAttributes.displayName == "" ?  text?.see_all_button : productsGroupData.productGroupItem[groupId].buttonAttributes.displayName}
                                        </button>
                                    </a>
                                </div>
                            )}   
                            {productsGroupData.productGroupItem[groupId].productList.length > 4 && (
                            <div className="flex w-1/3 justify-center mt-20">
                                <button
                                    aria-label="Previous" 
                                    className="
                                        embla__button embla__button--prev inline-flex items-center justify-center 
                                        whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none 
                                        focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none 
                                        disabled:opacity-50 text-base font-normal border bg-transparent h-10 w-10 rounded-full max-md:hidden 
                                        relative transform left-0 top-0 translate-y-0 mx-1 dynamic-bg dynamic-color dynamic-border"
                                    style={
                                        {
                                            "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                            "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                            "--dynamic-border": colorStyle?.buttonTextNormal || "#ffffff",
                                            "--dynamic-hover-border": colorStyle?.buttonTextHover || "#ffffff",
                                            "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                            "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                            "--dynamic-focus-color": colorStyle?.buttonTextNormal || "#ffffff",
                                            "--dynamic-hover-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                                        } as React.CSSProperties
                                    }
                                    onClick={scrollPrev}>
                                    <ChevronLeft 
                                        className="h-5 w-5"
                                    />
                                </button>
                                <button
                                    aria-label="Next"
                                    className="
                                        embla__button embla__button--next inline-flex items-center justify-center 
                                        whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none 
                                        focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none 
                                        disabled:opacity-50 text-base font-normal border bg-transparent h-10 w-10 rounded-full -right-12 max-md:hidden 
                                        relative transform left-0 top-0 translate-y-0 mx-1 dynamic-bg dynamic-color dynamic-border"
                                    style={
                                        {
                                            "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                            "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                            "--dynamic-border": colorStyle?.buttonTextNormal || "#ffffff",
                                            "--dynamic-hover-border": colorStyle?.buttonTextHover || "#ffffff",
                                            "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                            "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                            "--dynamic-focus-color": colorStyle?.buttonTextNormal || "#ffffff",
                                            "--dynamic-hover-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                                        } as React.CSSProperties
                                    }
                                    onClick={scrollNext}>
                                    <ChevronRight 
                                        className="h-5 w-5"
                                    />
                                </button>
                            </div>
                            )}
                        </div>
                    </div>
                </>    
                }
            </div>
        </div>
    )
}

export default ProductShowBoxes;